import React from 'react';
import { buildSrcSet, autoLegacySrcSet } from '../../lib/responsive';

/**
 * Imagen responsive con `<picture>` + AVIF + WebP + srcset/sizes.
 *
 * Props
 * - src              URL master (fallback si el navegador no entiende `<picture>`)
 * - variants         { "480": webpUrl, "960": webpUrl, ... }  (WebP)
 * - variantsAvif     { "480": avifUrl, ... }                  (AVIF · opcional)
 * - sizes            atributo `sizes` (e.g. "100vw" o "(min-width: 768px) 50vw, 100vw")
 * - alt              texto alternativo (obligatorio para accesibilidad)
 * - loading          "lazy" (default) | "eager" (above-the-fold)
 * - decoding         "async" (default) | "sync"
 * - srcSet           opcional · permite pasar un srcSet ya construido (e.g. para legacy)
 * - fetchPriority    "high" | "low" · React 19 camelCase
 *
 * Si no hay `variants` ni `srcSet` pero la URL casa con el patrón legacy
 * `/path/<id>-{thumb|medium|full}.webp`, se autogenera el srcset con las 3
 * variantes existentes en disco · cero coste para el caller.
 *
 * Orden en `<picture>`:
 *   1. <source type="image/avif"> — el navegador lo elige si lo soporta (~92% global 2026)
 *   2. <source type="image/webp"> — fallback universal
 *   3. <img src=…>                — último fallback (también recibe loading/lazy/etc.)
 */
export default function ResponsiveImage({
  src,
  variants,
  variantsAvif,
  sizes = '100vw',
  srcSet: srcSetOverride,
  alt = '',
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  className,
  style,
  ...rest
}) {
  if (!src && !srcSetOverride) return null;
  let webpSrcSet = srcSetOverride || buildSrcSet(variants);
  if (!webpSrcSet) webpSrcSet = autoLegacySrcSet(src, 'webp');
  let avifSrcSet = buildSrcSet(variantsAvif);
  if (!avifSrcSet) avifSrcSet = autoLegacySrcSet(src, 'avif');

  // Si no hay ningún srcset (ni AVIF ni WebP) renderiza un <img> simple.
  if (!webpSrcSet && !avifSrcSet) {
    return (
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        className={className}
        style={style}
        {...rest}
      />
    );
  }

  return (
    <picture style={{ display: 'contents' }}>
      {avifSrcSet && (
        <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      )}
      {webpSrcSet && (
        <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
      )}
      <img
        src={src}
        srcSet={webpSrcSet || undefined}
        sizes={(webpSrcSet || avifSrcSet) ? sizes : undefined}
        alt={alt}
        loading={loading}
        decoding={decoding}
        fetchPriority={fetchPriority}
        className={className}
        style={style}
        {...rest}
      />
    </picture>
  );
}
